import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { ProviderMatchDetails } from "@/lib/sports/providers/types";
import { normalizeMatchStatus, type NormalizedMatchStatus } from "@/lib/matchStatus";

export interface OfficialMatchHeaderData {
  details: ProviderMatchDetails | undefined;
  status: NormalizedMatchStatus;
  isOfficial: boolean;
  isLoading: boolean;
  isError: boolean;
  updatedAt: number | undefined;
}

/**
 * Real Data Phase 5 — Match Header hook.
 *
 * Consumes `unifiedSports.getMatchDetails(fixtureId)` — the only allowed
 * chain (API-Football → ApiFootballProvider → UnifiedSports → hook → UI).
 * Never generates a local clock or fabricates a score; the official
 * elapsed/status/scores returned by the provider always win.
 *
 * Refetch cadence adapts to the current lifecycle:
 *   • SCHEDULED         → 5 min
 *   • LIVE (regulation) → 30 s
 *   • HT / BT           → 60 s
 *   • FINISHED          → disabled after final payload
 */
export function useOfficialMatchHeader(
  fixtureId: string | undefined,
): OfficialMatchHeaderData {
  const enabled = typeof fixtureId === "string" && fixtureId.length > 0;
  const q = useQuery<ProviderMatchDetails>({
    queryKey: ["match-header", fixtureId ?? "__none__"],
    queryFn: () => unifiedSports.getMatchDetails(fixtureId as string),
    enabled,
    staleTime: 20_000,
    retry: 1,
    refetchInterval: (query) => {
      const data = query.state.data as ProviderMatchDetails | undefined;
      const norm = normalizeMatchStatus(data?.statusShort);
      if (norm.isFinished) return false;
      if (norm.isBreak) return 60_000;
      if (norm.isLive) return 30_000;
      return 5 * 60_000;
    },
  });

  const status = useMemo(
    () => normalizeMatchStatus(q.data?.statusShort ?? null),
    [q.data?.statusShort],
  );

  return {
    details: q.data,
    status,
    isOfficial: !!q.data?.official,
    isLoading: enabled && q.isLoading,
    isError: q.isError,
    updatedAt: q.dataUpdatedAt || undefined,
  };
}